import { Component, EventEmitter, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import {
  IAddServiceRequest,
  IServiceRequestCode,
  IServiceRequestDropdown,
} from 'src/app/interfaces/ServiceRequest/IServiceRequest';
import { ServiceRequestService } from 'src/app/services/serviceRequest.service';
import { SwalToastService } from 'src/app/services/swal.service';

type LookupField = 'category' | 'procedureCode' | 'procedureReason';

interface LookupState {
  options: IServiceRequestCode[];
  searchTerm: string;
  pageNumber: number;
  totalRecords: number | null;
  isLoading: boolean;
  requestSequence: number;
  bottomLatch: boolean;
}

function occurrenceValidator(control: AbstractControl): ValidationErrors | null {
  const occurrence = control.get('occurrence')?.value as Date[] | null;
  const start = occurrence?.[0];
  const end = occurrence?.[1];

  if (!start || !end) {
    return null;
  }

  return new Date(end).getTime() < new Date(start).getTime()
    ? { invalidOccurrence: true }
    : null;
}

@Component({
  selector: 'app-service-request-add',
  templateUrl: './service-request-add.component.html',
  styleUrls: ['./service-request-add.component.css'],
  standalone: false,
})
export class ServiceRequestAddComponent {
  @Output() cancelled = new EventEmitter<void>();
  @Output() created = new EventEmitter<number>();

  private destroy$ = new Subject<void>();
  private readonly lookupPageSize = 20;
  private readonly lookupScrollThresholdPx = 24;
  private readonly dropdownClassNames: Record<LookupField, string> = {
    category: 'service-request-category-dropdown',
    procedureCode: 'service-request-procedure-code-dropdown',
    procedureReason: 'service-request-procedure-reason-dropdown',
  };
  private readonly lookupScrollCleanup = new Map<LookupField, () => void>();

  isLoading = false;
  isLookupLoading = false;
  serviceRequestSubmitted = false;

  dropdownData?: IServiceRequestDropdown;
  categoryState: LookupState = this.createLookupState();
  procedureCodeState: LookupState = this.createLookupState();
  procedureReasonState: LookupState = this.createLookupState();

  serviceRequestForm: FormGroup = new FormGroup({});

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly serviceRequestService: ServiceRequestService,
    private readonly swalToastService: SwalToastService,
  ) {}

  ngOnInit() {
    this.serviceRequestForm = this.formBuilder.group(
      {
        statusId: [null, Validators.required],
        intentId: [null, Validators.required],
        priorityId: [null],
        categoryId: [null],
        procedureCodeId: [null, Validators.required],
        procedureReasonId: [null],
        occurrence: [null],
        asNeeded: [false],
        description: [null],
        orderDetailValue: [null],
        notes: [null],
      },
      { validators: occurrenceValidator },
    );

    this.loadLookupData();
  }

  ngOnDestroy() {
    this.detachLookupScrollListeners();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasInvalidOccurrence(): boolean {
    return (
      this.serviceRequestSubmitted &&
      this.serviceRequestForm.hasError('invalidOccurrence')
    );
  }

  handleCancel() {
    this.cancelled.emit();
  }

  submitForm(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.serviceRequestSubmitted = true;
    this.serviceRequestForm.markAllAsTouched();

    if (this.serviceRequestForm.invalid) {
      return;
    }

    const occurrence = this.serviceRequestForm.value.occurrence as Date[] | null;
    const request: IAddServiceRequest = {
      statusId: Number(this.serviceRequestForm.value.statusId),
      intentId: Number(this.serviceRequestForm.value.intentId),
      procedureCodeId: Number(this.serviceRequestForm.value.procedureCodeId),
      priorityId: this.toNullableNumber(this.serviceRequestForm.value.priorityId),
      categoryId: this.toNullableNumber(this.serviceRequestForm.value.categoryId),
      procedureReasonId: this.toNullableNumber(
        this.serviceRequestForm.value.procedureReasonId,
      ),
      description: this.normalizeOptionalText(
        this.serviceRequestForm.value.description,
      ),
      orderDetailValue: this.normalizeOptionalText(
        this.serviceRequestForm.value.orderDetailValue,
      ),
      asNeeded: this.serviceRequestForm.value.asNeeded ? true : null,
      occurrenceStart: occurrence?.[0]
        ? new Date(occurrence[0]).toISOString()
        : null,
      occurrenceEnd: occurrence?.[1]
        ? new Date(occurrence[1]).toISOString()
        : null,
      notes: this.normalizeOptionalText(this.serviceRequestForm.value.notes),
    };

    this.isLoading = true;
    this.serviceRequestService.addServiceRequest(request).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.swalToastService.toastSuccess(
          'Service request added successfully',
        );
        this.created.emit(response.data);
      },
      error: (err) => {
        this.isLoading = false;
        this.swalToastService.toastError(
          err?.error?.message || 'Unable to add Service Request',
        );
      },
    });
  }

  private loadLookupData() {
    this.isLookupLoading = true;
    this.serviceRequestService
      .getDropdownData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (dropdownData) => {
          this.dropdownData = dropdownData.data;
          this.isLookupLoading = false;
          this.applyDefaults();
          this.fetchLookupPage('category', 1, '', true);
          this.fetchLookupPage('procedureCode', 1, '', true);
          this.fetchLookupPage('procedureReason', 1, '', true);
        },
        error: (err) => {
          this.isLookupLoading = false;
          this.swalToastService.toastError(
            err?.error?.message || 'Unable to retrieve service request lookups',
          );
        },
      });
  }

  onCategorySearch(searchTerm: string) {
    this.fetchLookupPage('category', 1, searchTerm, true);
  }

  onProcedureCodeSearch(searchTerm: string) {
    this.fetchLookupPage('procedureCode', 1, searchTerm, true);
  }

  onProcedureReasonSearch(searchTerm: string) {
    this.fetchLookupPage('procedureReason', 1, searchTerm, true);
  }

  loadMoreCategories() {
    this.loadNextLookupPage('category');
  }

  loadMoreProcedureCodes() {
    this.loadNextLookupPage('procedureCode');
  }

  loadMoreProcedureReasons() {
    this.loadNextLookupPage('procedureReason');
  }

  onCategoryOpenChange(isOpen: boolean) {
    this.handleLookupOpenChange('category', isOpen);
  }

  onProcedureCodeOpenChange(isOpen: boolean) {
    this.handleLookupOpenChange('procedureCode', isOpen);
  }

  onProcedureReasonOpenChange(isOpen: boolean) {
    this.handleLookupOpenChange('procedureReason', isOpen);
  }

  private applyDefaults() {
    if (!this.dropdownData) {
      return;
    }

    const activeStatus = this.dropdownData.statuses.find(
      (status) => status.code.toLowerCase() === 'active',
    );
    const planIntent = this.dropdownData.intents.find(
      (intent) => intent.code.toLowerCase() === 'plan',
    );

    this.serviceRequestForm.patchValue({
      statusId: this.serviceRequestForm.value.statusId ?? activeStatus?.id ?? null,
      intentId: this.serviceRequestForm.value.intentId ?? planIntent?.id ?? null,
    });
  }

  private normalizeOptionalText(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private createLookupState(): LookupState {
    return {
      options: [],
      searchTerm: '',
      pageNumber: 0,
      totalRecords: null,
      isLoading: false,
      requestSequence: 0,
      bottomLatch: false,
    };
  }

  private loadNextLookupPage(field: LookupField) {
    const state = this.getLookupState(field);

    if (state.isLoading || !this.hasMoreLookupResults(state)) {
      return;
    }

    this.fetchLookupPage(
      field,
      state.pageNumber + 1,
      state.searchTerm,
      false,
    );
  }

  private fetchLookupPage(
    field: LookupField,
    pageNumber: number,
    searchTerm: string,
    replace: boolean,
  ) {
    const state = this.getLookupState(field);
    const normalizedSearchTerm = searchTerm.trim();
    const requestSequence = state.requestSequence + 1;

    state.requestSequence = requestSequence;
    state.isLoading = true;
    state.bottomLatch = true;

    this.getLookupRequest(field, normalizedSearchTerm, pageNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (state.requestSequence !== requestSequence) {
            return;
          }

          const nextOptions = this.preserveSelectedOption(
            field,
            response.data ?? [],
          );

          state.options = replace
            ? nextOptions
            : this.mergeLookupOptions(state.options, nextOptions);
          state.searchTerm = normalizedSearchTerm;
          state.pageNumber = pageNumber;
          state.totalRecords = response.totalRecords ?? null;
          state.isLoading = false;
        },
        error: (err) => {
          if (state.requestSequence !== requestSequence) {
            return;
          }

          state.isLoading = false;
          state.bottomLatch = false;
          this.swalToastService.toastError(
            err?.error?.message || 'Unable to retrieve service request lookups',
          );
        },
      });
  }

  private getLookupRequest(
    field: LookupField,
    searchTerm: string,
    pageNumber: number,
  ) {
    switch (field) {
      case 'category':
        return this.serviceRequestService.getCategoryDropdownData(
          searchTerm,
          pageNumber,
          this.lookupPageSize,
        );
      case 'procedureCode':
        return this.serviceRequestService.getProcedureCodeDropdownData(
          searchTerm,
          pageNumber,
          this.lookupPageSize,
        );
      case 'procedureReason':
        return this.serviceRequestService.getProcedureReasonDropdownData(
          searchTerm,
          pageNumber,
          this.lookupPageSize,
        );
    }
  }

  private getLookupState(field: LookupField): LookupState {
    switch (field) {
      case 'category':
        return this.categoryState;
      case 'procedureCode':
        return this.procedureCodeState;
      case 'procedureReason':
        return this.procedureReasonState;
    }
  }

  private hasMoreLookupResults(state: LookupState): boolean {
    if (state.totalRecords === null) {
      return state.options.length >= state.pageNumber * this.lookupPageSize;
    }

    return state.options.length < state.totalRecords;
  }

  private mergeLookupOptions(
    currentOptions: IServiceRequestCode[],
    nextOptions: IServiceRequestCode[],
  ): IServiceRequestCode[] {
    const mergedOptions = [...currentOptions];
    const existingIds = new Set(currentOptions.map((option) => option.id));

    nextOptions.forEach((option) => {
      if (existingIds.has(option.id)) {
        return;
      }

      existingIds.add(option.id);
      mergedOptions.push(option);
    });

    return mergedOptions;
  }

  private preserveSelectedOption(
    field: LookupField,
    options: IServiceRequestCode[],
  ): IServiceRequestCode[] {
    const controlName = this.getLookupControlName(field);
    const selectedValue = this.toNullableNumber(
      this.serviceRequestForm.get(controlName)?.value,
    );

    if (!selectedValue) {
      return options;
    }

    if (options.some((option) => option.id === selectedValue)) {
      return options;
    }

    const existingSelectedOption = this.getLookupState(field).options.find(
      (option) => option.id === selectedValue,
    );

    return existingSelectedOption
      ? [existingSelectedOption, ...options]
      : options;
  }

  private getLookupControlName(field: LookupField): string {
    switch (field) {
      case 'category':
        return 'categoryId';
      case 'procedureCode':
        return 'procedureCodeId';
      case 'procedureReason':
        return 'procedureReasonId';
    }
  }

  private handleLookupOpenChange(field: LookupField, isOpen: boolean) {
    this.detachLookupScrollListener(field);

    if (!isOpen) {
      return;
    }

    window.setTimeout(() => {
      this.attachLookupScrollListener(field);
    });
  }

  private attachLookupScrollListener(field: LookupField) {
    const scrollContainer = this.getLookupScrollContainer(field);

    if (!scrollContainer) {
      return;
    }

    const onScroll = () => this.checkLookupScrollPosition(field);
    scrollContainer.addEventListener('scroll', onScroll, { passive: true });

    this.lookupScrollCleanup.set(field, () => {
      scrollContainer.removeEventListener('scroll', onScroll);
    });
  }

  private checkLookupScrollPosition(field: LookupField) {
    const scrollContainer = this.getLookupScrollContainer(field);

    if (!scrollContainer) {
      return;
    }

    const isNearBottom =
      scrollContainer.scrollTop + scrollContainer.clientHeight >=
      scrollContainer.scrollHeight - this.lookupScrollThresholdPx;

    if (!isNearBottom) {
      this.getLookupState(field).bottomLatch = false;
      return;
    }

    if (!this.getLookupState(field).bottomLatch) {
      this.loadNextLookupPage(field);
    }
  }

  private getLookupScrollContainer(field: LookupField): HTMLElement | null {
    const dropdownClassName = this.dropdownClassNames[field];

    return document.querySelector(
      `.${dropdownClassName} .cdk-virtual-scroll-viewport`,
    ) as HTMLElement | null;
  }

  private detachLookupScrollListeners() {
    this.lookupScrollCleanup.forEach((cleanup) => cleanup());
    this.lookupScrollCleanup.clear();
  }

  private detachLookupScrollListener(field: LookupField) {
    this.lookupScrollCleanup.get(field)?.();
    this.lookupScrollCleanup.delete(field);
  }
}
